import { IconFilter16 } from '@warp-ds/icons/react/filter-16';
import { IconSorting16 } from '@warp-ds/icons/react/sorting-16';
import { Button } from '@warp-ds/react/components/button';
import { Modal } from '@warp-ds/react/components/modal';
import { Select } from '@warp-ds/react/components/select';
import { Toggle, type ToggleEntry } from '@warp-ds/react/components/toggle';
import { type ChangeEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SortDetails } from 'shared/page/SortDetails';
import { thousandSeparated } from 'shared/util/formatting';
import { Breadcrumbs } from '@/components/breadcrumbs/Breadcrumbs';
import type { PaleState, ThunkDispatchFunc } from '@/interfaces';
import displayValues from '@/resources/displayValues.json';
import { actions as appStateActions } from '@/state/appStateActions';
import { actions as filterActions } from '@/state/filterActions';
import { fetchResults } from '@/state/resultActions';

const sorting: Record<string, string> = displayValues.sorting;

export const TopBar = () => {
    const dispatch = useDispatch<ThunkDispatchFunc>();
    const [sortModalOpen, setSortModalOpen] = useState(false);

    const filtersOpen = useSelector((state: PaleState) => state.appState.filtersOpen);
    const numberOfOffers = useSelector((state: PaleState) => state.results.numberOfHotels);
    const sortOptions = useSelector((state: PaleState) => state.results.sortOptions);
    const defaultSort = useSelector((state: PaleState) => state.results.defaultSort);
    const selectedSort = useSelector((state: PaleState) => state.filters.sort);
    const currentSort = selectedSort ?? defaultSort;

    const validSortOptions = (sortOptions ?? []).filter((option) => Boolean(sorting[option]));

    const sortToggleOptions: ToggleEntry[] = validSortOptions.map((option) => ({
        label: sorting[option],
        value: option,
    }));

    const selectedSortToggle: ToggleEntry[] = sorting[currentSort] ? [{ label: sorting[currentSort], value: currentSort }] : [];

    const onSortDropdownChange = (event: ChangeEvent<HTMLSelectElement>): void => {
        const value = event.target.value;
        dispatch(filterActions.changeSort(value === defaultSort ? undefined : value));
        dispatch(fetchResults());
    };

    const onSortToggleChange = (entry: ToggleEntry): void => {
        dispatch(filterActions.changeSort(entry.value === defaultSort ? undefined : entry.value));
        dispatch(fetchResults());
        setSortModalOpen(false);
    };

    return (
        <>
            <section
                data-testid="resultTopBar"
                className="fixed top-[50px] left-0 right-0 z-10 py-16 px-16 s-bg border-b md:relative md:grid md:grid-cols-3 md:gap-32 md:px-0 md:inset-auto! md:border-b-0">
                <div className="hidden md:block md:col-span-1">
                    <Breadcrumbs vertical="pp" />
                </div>
                <div className="flex justify-between items-center gap-8 md:col-span-2">
                    <h2 className="sr-only">Resultatinnstillinger</h2>
                    <Button
                        primary={filtersOpen}
                        utility={!filtersOpen}
                        small
                        onClick={() => dispatch(appStateActions.openFilter(!filtersOpen))}
                        aria-label={filtersOpen ? 'Vis resultat' : 'Vis filter'}
                        className="flex items-center gap-8 md:hidden"
                        data-testid="showFilterButton">
                        <IconFilter16 aria-hidden />
                        {filtersOpen ? 'Resultat' : 'Filter'}
                    </Button>
                    <p className="text-center text-s md:text-m m-0" data-testid="resultCount">
                        <span className="font-bold">{numberOfOffers ? thousandSeparated(numberOfOffers) : '0'}</span> ulike hoteller
                    </p>
                    <SortDetails section="pakkereiser" className="hidden md:flex" />
                    <Button
                        utility
                        small
                        onClick={() => setSortModalOpen(true)}
                        className="flex items-center gap-8 md:hidden"
                        data-testid="sortButton"
                        aria-haspopup="dialog"
                        aria-expanded={sortModalOpen}>
                        <IconSorting16 aria-hidden />
                        {sorting[currentSort]}
                    </Button>
                    <Select
                        className="hidden md:block"
                        aria-label="Sorter på"
                        value={currentSort}
                        onChange={onSortDropdownChange}
                        data-testid="sortDropdown">
                        {validSortOptions.map((option) => (
                            <option key={option} value={option}>
                                {sorting[option]}
                            </option>
                        ))}
                    </Select>
                </div>
            </section>
            <Modal open={sortModalOpen} onDismiss={() => setSortModalOpen(false)} title="Sorter etter" right>
                <Toggle
                    type="radio"
                    options={sortToggleOptions}
                    selected={selectedSortToggle}
                    onChange={onSortToggleChange}
                    className="flex flex-col gap-8"
                />
            </Modal>
        </>
    );
};
